import { MONITOR_IDENTIFIER_LABELS } from '@/components/monitors/lib/targetCatalog'

import {
  MONITOR_RECIPE_TARGETS,
  type CatalogRecipe,
  type MonitorChannel,
  type MonitorChip,
  type MonitorDashboardItem,
  type MonitorOverviewOut,
  type MonitorOwner,
  type MonitorRecipeTarget,
} from '@/types/Monitor'
import type { User } from '@/types/User'

/** Wire contract: 30 UTC-day counts. Fallback when a merged PATCH/POST cache
 * entry lacks the overview stats. */
export const DAILY_FEED_ENTRY_COUNT_DAYS = 30

// Frozen: this one array is shared by reference across every stats-less row,
// so an accidental in-place mutation would corrupt all of them at once.
const DEFAULT_DAILY_COUNTS: readonly number[] = Object.freeze(
  new Array<number>(DAILY_FEED_ENTRY_COUNT_DAYS).fill(0),
)

export const SYSTEM_OWNER: MonitorOwner = {
  userId: null,
  name: 'Sweetspot',
}

export const formatRelativeShort = (date: Date): string => {
  const diffMs = Date.now() - date.getTime()
  const minutes = Math.round(diffMs / 60_000)
  if (minutes < 1) return 'just now'
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.round(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.round(hours / 24)
  if (days < 7) return `${days}d ago`
  const weeks = Math.round(days / 7)
  if (weeks < 5) return `${weeks}w ago`
  const months = Math.round(days / 30)
  return `${months}mo ago`
}

export const buildOwnerMap = (
  users: User[] | undefined,
): Map<string, MonitorOwner> => {
  const owners = new Map<string, MonitorOwner>()
  for (const user of users ?? []) {
    if (user.id === 'guest') continue
    owners.set(user.id, {
      userId: user.id,
      name: `${user.first_name} ${user.last_name}`.trim() || user.email,
    })
  }
  return owners
}

const MONITOR_RECIPE_TARGET_SET = new Set<string>(MONITOR_RECIPE_TARGETS)

/** Parse the target facet out of a "monitor.{target}.{kind}" recipe key. Used
 * as the fallback when the live catalog hasn't loaded (or the key is unknown
 * to it). */
export const monitorRecipeTargetFromKey = (
  recipeKey: string,
): MonitorRecipeTarget | null => {
  const [prefix, target] = recipeKey.split('.')
  return prefix === 'monitor' &&
    target !== undefined &&
    MONITOR_RECIPE_TARGET_SET.has(target)
    ? (target as MonitorRecipeTarget)
    : null
}

// The recipe's required target-identifier params, with display labels shared
// with the wizard and workspace via MONITOR_IDENTIFIER_LABELS (plain titleCase
// would render "Uei"/"Idv Key"). Web recipes add their own identifiers.
const IDENTIFIER_PARAM_LABELS: Record<string, string> = {
  ...MONITOR_IDENTIFIER_LABELS,
  query: 'Query',
  url: 'URL',
}

const ACRONYM_WORDS: Record<string, string> = {
  naics: 'NAICS',
  psc: 'PSC',
  uei: 'UEI',
  idv: 'IDV',
  ota: 'OTA',
  otidv: 'OTIDV',
  id: 'ID',
  url: 'URL',
  cfda: 'CFDA',
}

const titleCase = (value: string): string =>
  value
    .split('_')
    .map(
      (word) =>
        ACRONYM_WORDS[word] ?? word.charAt(0).toUpperCase() + word.slice(1),
    )
    .join(' ')

// Scope fields whose mechanical Title Case would leak wire jargon ("Gte").
const SCOPE_FIELD_LABELS: Record<string, string> = {
  obligated_gte: 'Min Obligated',
  fiscal_year_gte: 'Min Fiscal Year',
  naics_code: 'NAICS',
  naics_starts_with: 'NAICS Prefix',
}

const scopeFieldLabel = (key: string): string =>
  SCOPE_FIELD_LABELS[key] ?? titleCase(key)

const formatChipValue = (value: string | number | boolean): string =>
  typeof value === 'number' ? value.toLocaleString('en-US') : String(value)

const isChipScalar = (value: unknown): value is string | number | boolean =>
  typeof value === 'string' ||
  typeof value === 'number' ||
  typeof value === 'boolean'

const formatScopeFieldValues = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value.filter(isChipScalar).map(formatChipValue)
  }
  if (value === null || value === undefined || value === '') return []
  if (isChipScalar(value)) return [formatChipValue(value)]
  return []
}

/**
 * Build display chips from `recipe_params`: the target identifier first, then
 * one chip per non-empty scope field. Scope fields with the same name across
 * topics (e.g. `contract_awarded.naics` and `idv_awarded.naics`) merge into a
 * single chip with deduped values.
 */
export const buildRecipeParamChips = (
  recipeParams: Record<string, unknown>,
): MonitorChip[] => {
  const chips: MonitorChip[] = []
  const scopeChipValues = new Map<string, string[]>()

  for (const [paramKey, paramValue] of Object.entries(recipeParams)) {
    if (paramKey === 'recipe_key') continue

    const identifierLabel = IDENTIFIER_PARAM_LABELS[paramKey]
    if (identifierLabel !== undefined) {
      if (isChipScalar(paramValue) && paramValue !== '') {
        chips.push({
          key: identifierLabel,
          values: [formatChipValue(paramValue)],
        })
      }
      continue
    }

    // Remaining params are topic scope objects ({naics: [...], psc: null, …}).
    if (
      typeof paramValue !== 'object' ||
      paramValue === null ||
      Array.isArray(paramValue)
    ) {
      continue
    }
    for (const [scopeKey, scopeValue] of Object.entries(paramValue)) {
      const values = formatScopeFieldValues(scopeValue)
      if (!values.length) continue
      const label = scopeFieldLabel(scopeKey)
      const existing = scopeChipValues.get(label)
      if (existing) {
        for (const value of values) {
          if (!existing.includes(value)) existing.push(value)
        }
      } else {
        scopeChipValues.set(label, [...values])
      }
    }
  }

  for (const [key, values] of scopeChipValues) {
    chips.push({ key, values })
  }
  return chips
}

export const toDashboardMonitor = (
  monitor: MonitorOverviewOut,
  owners: Map<string, MonitorOwner>,
  recipesByKey?: Map<string, CatalogRecipe>,
): MonitorDashboardItem => {
  const owner =
    (monitor.created_by_user_id && owners.get(monitor.created_by_user_id)) ||
    SYSTEM_OWNER

  const subscriberIds = monitor.email_subscriber_ids ?? []
  const channels: MonitorChannel[] = []
  if (subscriberIds.length > 0) channels.push('email')
  if (monitor.slack_channel_id !== null) channels.push('slack')

  // The live recipe catalog can ship targets this build doesn't know yet —
  // validate before trusting it so one unknown target can't crash consumers
  // that index MONITOR_TARGET_THEME.
  const catalogTarget = recipesByKey?.get(monitor.recipe_key)?.target

  return {
    id: monitor.id,
    name: monitor.name,
    chips: buildRecipeParamChips(monitor.recipe_params),
    channels,
    owner,
    status: monitor.status,
    unreadCount: monitor.unread_count ?? 0,
    dailyCounts: monitor.daily_feed_entry_counts ?? DEFAULT_DAILY_COUNTS,
    latestEntryAt: monitor.latest_entry?.event_at ?? null,
    targetKind:
      (catalogTarget && MONITOR_RECIPE_TARGET_SET.has(catalogTarget)
        ? catalogTarget
        : null) ?? monitorRecipeTargetFromKey(monitor.recipe_key),
  }
}
